import { Link } from "react-router-dom";
import { useClassrooms } from "../data/ClassroomContext";
import { useLessons } from "../data/LessonsContext";
import useProgress from "../data/useProgress";
import { COMPLETED } from "../data/progressStore";
import { CheckIcon } from "../components/Icons";
import { orderForToday } from "../components/LessonState";
import {
  LoadingCards,
  Message,
  ProblemMessage,
  SavedOnDeviceNotice,
} from "../components/Status";
import ui from "../components/ui.module.css";

// Where the teacher is: class, then subject and week when they are known.
export const teachingContext = ({ className, subject, week }) =>
  [className, subject, week && `Week ${week}`].filter(Boolean).join(" · ");

export const subjectPath = (subjectKey) => `/subject/${subjectKey}`;

const WeekChooser = ({ weeks, onChoose }) => (
  <div className={ui.stack}>
    <h2 className={ui.cardTitle}>Which week are you teaching?</h2>
    <p className={ui.meta}>We'll remember this for next time.</p>
    <ul className={ui.list}>
      {weeks.map((week) => (
        <li key={week}>
          <button
            type="button"
            className={`${ui.button} ${ui.secondary} ${ui.full}`}
            onClick={() => onChoose(week)}
          >
            Week {week}
          </button>
        </li>
      ))}
    </ul>
  </div>
);

// What Today and Week show until there is a week of lessons to show; null
// once there is.
export const LessonsNotReady = ({ lessons, className }) => {
  if (lessons.status === "loading") return <LoadingCards />;
  if (lessons.status === "error") {
    return (
      <ProblemMessage
        problem={lessons.problem}
        onRetry={lessons.retry}
        autoRetrying={lessons.autoRetrying}
      />
    );
  }
  if (lessons.lessons.length === 0) {
    return (
      <Message
        title="No lessons are ready yet"
        actions={
          <button
            type="button"
            className={`${ui.button} ${ui.secondary}`}
            onClick={lessons.retry}
          >
            Check again
          </button>
        }
      >
        Lessons appear here once they're approved for {className}. We'll keep checking automatically.
      </Message>
    );
  }
  if (!lessons.week) return <WeekChooser weeks={lessons.weeks} onChoose={lessons.chooseWeek} />;
  return null;
};

export const OfflineLessonsNotice = ({ lessons }) =>
  lessons.fromCache && lessons.problem ? (
    <SavedOnDeviceNotice>
      You're offline. Showing lessons saved on this device.
    </SavedOnDeviceNotice>
  ) : null;

// One card per subject: how far the class is, and what comes next.
const SubjectCard = ({ subject, getProgress }) => {
  const done = subject.lessons.filter(
    (lesson) => getProgress(lesson.id)?.status === COMPLETED
  ).length;
  const total = subject.lessons.length;
  const [next] = orderForToday(subject.lessons, getProgress);
  const allDone = done === total;

  return (
    <Link
      to={subjectPath(subject.key)}
      className={`${ui.card} ${ui.subjectCard}`}
      aria-label={`${subject.name}: ${done} of ${total} lessons done`}
    >
      <span className={ui.stack} style={{ gap: "0.25rem" }}>
        <span className={ui.cardTitle}>{subject.name}</span>
        <span className={ui.meta}>
          {allDone ? (
            <span className={`${ui.state} ${ui.done}`}>
              <CheckIcon size={16} />
              All done
            </span>
          ) : (
            <>
              {done} of {total} lessons done · Next: {next.title}
            </>
          )}
        </span>
      </span>
      <span className={ui.chevron} aria-hidden="true">›</span>
    </Link>
  );
};

// The week's curriculum: every subject, and a way to another week.
const Week = () => {
  const lessons = useLessons();
  const progress = useProgress();
  const { active } = useClassrooms();

  return (
    <div className={ui.page}>
      <div className={ui.stack}>
        <h1 className={ui.title}>{lessons.week ? `Week ${lessons.week}` : "Week"}</h1>
        <p className={ui.eyebrow}>{active.name}</p>
      </div>
      {lessons.week && lessons.weeks.length > 1 && (
        <div className={ui.quickActions}>
          <button
            type="button"
            className={`${ui.button} ${ui.secondary}`}
            onClick={() => lessons.chooseWeek(null)}
          >
            Change week
          </button>
        </div>
      )}
      <OfflineLessonsNotice lessons={lessons} />
      {LessonsNotReady({ lessons, className: active.name }) || (
        <section className={ui.stack} aria-labelledby="subjects">
          <h2 id="subjects" className={ui.cardTitle}>
            Week {lessons.week} subjects
          </h2>
          <ul className={ui.list}>
            {lessons.subjects.map((subject) => (
              <li key={subject.key}>
                <SubjectCard subject={subject} getProgress={progress.get} />
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
};

export default Week;
